import React, { useEffect, useState } from "react";
import * as FaIcons from "react-icons/fa";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import { apiUrl } from "../ApiManager/AxiosHeaders";

const EMPTY_FORM = {
  document_name: "",
  start_date: "",
  end_date: "",
  status: "active",
  notes: "",
};

function UploadProviderContract({
  provider_id,
  embedded = false,
  showUploadModal: showUploadModalProp,
  onUploadModalChange,
  onContractsChange,
  expandAllContracts = false,
  hideContractList = false,
  skipContractFetch = false,
  uploadFormOnly = false,
}) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [showAllContracts, setShowAllContracts] = useState(false);
  const [internalShowUploadModal, setInternalShowUploadModal] = useState(false);

  const isUploadModalControlled = typeof onUploadModalChange === "function";
  const showUploadModal = isUploadModalControlled
    ? Boolean(showUploadModalProp)
    : internalShowUploadModal;
  const setShowUploadModal = isUploadModalControlled
    ? onUploadModalChange
    : setInternalShowUploadModal;

  const user = JSON.parse(localStorage.getItem("user"));
  const fileInputId = `contractFileInput-${provider_id || "default"}`;

  const resetUploadForm = () => {
    setFile(null);
    setFormData({ ...EMPTY_FORM });
    setIsDragging(false);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (
      selectedFile &&
      (selectedFile.type === "application/pdf" ||
        selectedFile.type.startsWith("image/"))
    ) {
      setFile(selectedFile);
    } else {
      Swal.fire("Warning", "Only PDF and image files are allowed.", "warning");
    }
    e.target.value = "";
  };

  const removeFile = () => {
    resetUploadForm();
  };

  const closeUploadModal = () => {
    if (uploading) return;
    setShowUploadModal(false);
    resetUploadForm();
  };

  useEffect(() => {
    if (!skipContractFetch) {
      fetchContracts();
    }
  }, [provider_id, skipContractFetch]);

  const fetchContracts = async () => {
    try {
      const response = await apiUrl.get(
        `get-provider-contracts/${provider_id}`,
      );
      if (response?.data?.status) {
        const nextContracts = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        setContracts(nextContracts);
        onContractsChange?.(nextContracts);
      } else {
        setContracts([]);
        onContractsChange?.([]);
      }
    } catch (error) {
      console.error(error);
      setContracts([]);
      onContractsChange?.([]);
    }
  };

  const uploadContract = async () => {
    if (!file) {
      Swal.fire("Error", "Please select a file.", "error");
      return;
    }
    if (!formData.document_name.trim()) {
      Swal.fire("Error", "Document name is required.", "error");
      return;
    }
    if (!formData.start_date) {
      Swal.fire("Error", "Start date is required.", "error");
      return;
    }

    const url = "upload-provider-contract";
    const formPayload = new FormData();

    formPayload.append("provider_id", provider_id);
    formPayload.append("file", file);
    formPayload.append("document_name", formData.document_name.trim());
    formPayload.append("start_date", formData.start_date);
    formPayload.append("end_date", formData.end_date || "");
    formPayload.append("status", formData.status);
    formPayload.append("notes", formData.notes.trim());
    formPayload.append("uploaded_by", user?.id || "");

    setUploading(true);
    try {
      const response = await apiUrl.post(url, formPayload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response?.data?.status) {
        Swal.fire("Success", "Contract uploaded successfully.", "success");
        resetUploadForm();
        if (embedded) {
          setShowUploadModal(false);
        }
        fetchContracts();
      } else {
        throw new Error(response?.data?.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || error.message || "Upload failed.",
        "error",
      );
    } finally {
      setUploading(false);
    }
  };

  const updateStatus = async (contractId, newStatus) => {
    try {
      const response = await apiUrl.put(
        `update-provider-contract-status/${contractId}`,
        { status: newStatus },
      );
      if (response?.data?.status) {
        Swal.fire("Success", "Status updated.", "success");
        fetchContracts();
      }
    } catch (error) {
      Swal.fire("Error", "Failed to update status.", "error");
    }
  };

  const handleDelete = async (contract) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `Do you really want to delete "${contract.document_name}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const response = await apiUrl.delete(
        `delete-provider-contract/${contract.id}`,
      );

      if (response.data.status) {
        fetchContracts();
        Swal.fire({
          title: `Discount Rule "${contract.document_name}" deleted successfully!`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
          background: "#eef2f8",
          iconColor: "#435690",
        });
      }
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({
        title: "Failed to delete discount rule",
        text: error.response?.data?.message || "An error occurred",
        icon: "error",
        timer: 3000,
        showConfirmButton: true,
      });
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    return new Date(dateValue)
      .toLocaleDateString("en-GB")
      .replace(/\//g, "-");
  };

  const showFullContractList = showAllContracts || expandAllContracts;
  const visibleContracts =
    embedded && !showFullContractList ? contracts.slice(0, 1) : contracts;

  const renderUploadForm = () => (
    <>
      <div
        className={`cms-provider-contract-dropzone ${
          isDragging ? "is-dragging" : ""
        }`}
        onClick={() => document.getElementById(fileInputId)?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);

          const droppedFile = e.dataTransfer.files[0];
          if (
            droppedFile &&
            (droppedFile.type === "application/pdf" ||
              droppedFile.type.startsWith("image/"))
          ) {
            setFile(droppedFile);
          } else {
            Swal.fire(
              "Warning",
              "Only PDF and image files are allowed.",
              "warning",
            );
          }
        }}
      >
        <input
          id={fileInputId}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={handleFileSelect}
        />
        <div className="cms-provider-contract-dropzone-icon">
          <FaIcons.FaCloudUploadAlt size={embedded ? 16 : 20} />
        </div>
        <p className="cms-provider-contract-dropzone-title">
          Drag & drop contract here
        </p>
        <p className="cms-provider-contract-dropzone-sub">
          or <strong>browse</strong> from your computer
        </p>
      </div>

      {file && (
        <div className="cms-provider-contract-file-preview">
          <div className="cms-provider-contract-file-info">
            <FaIcons.FaFilePdf size={22} color="#ef4444" />
            <div>
              <p className="cms-provider-contract-file-name">{file.name}</p>
              <p className="cms-provider-contract-file-size">
                {Math.round(file.size / 1024)} KB
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={removeFile}
            className="cms-provider-contract-remove-btn"
            title="Remove file"
          >
            <FaIcons.FaTrashAlt size={11} />
          </button>
        </div>
      )}

      {file && (
        <div className="cms-provider-contract-form">
          <div className="cms-provider-contract-form-row">
            <div className="cms-provider-contract-field">
              <label className="cms-provider-contract-field-label">
                Document Name <span className="required">*</span>
              </label>
              <input
                type="text"
                value={formData.document_name}
                onChange={(e) =>
                  setFormData({ ...formData, document_name: e.target.value })
                }
                className="cms-provider-contract-input"
                placeholder="e.g., Service Agreement 2024"
              />
            </div>

            <div className="cms-provider-contract-field">
              <label className="cms-provider-contract-field-label">Status</label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className="cms-provider-contract-select"
              >
                <option value="active">Active</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            <div className="cms-provider-contract-field">
              <label className="cms-provider-contract-field-label">
                Start Date <span className="required">*</span>
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) =>
                  setFormData({ ...formData, start_date: e.target.value })
                }
                className="cms-provider-contract-input"
              />
            </div>

            <div className="cms-provider-contract-field">
              <label className="cms-provider-contract-field-label">End Date</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) =>
                  setFormData({ ...formData, end_date: e.target.value })
                }
                className="cms-provider-contract-input"
              />
            </div>
          </div>

          <div className="cms-provider-contract-notes-row">
            <label className="cms-provider-contract-field-label">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows="3"
              className="cms-provider-contract-textarea"
              placeholder="Optional notes about this contract..."
            />
          </div>
        </div>
      )}

      {file && (
        <div className="cms-provider-contract-upload-wrap">
          <button
            type="button"
            className="cms-provider-contract-upload-btn"
            onClick={uploadContract}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <FaIcons.FaSpinner className="cms-provider-contract-spinner" size={12} />
                Uploading...
              </>
            ) : (
              <>
                <FaIcons.FaCloudUploadAlt size={12} />
                Upload Contract
              </>
            )}
          </button>
        </div>
      )}
    </>
  );

  if (uploadFormOnly) {
    return renderUploadForm();
  }

  return (
    <>
      {!embedded && (
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center shadow-sm text-blue-600 text-lg font-bold">
            <FaIcons.FaFileContract />
          </div>
          <h1 className="text-2xl font-bold text-blue-600 tracking-tight">
            Provider Contract
          </h1>
        </div>
      )}

      {/* Inline upload form only when not embedded (standalone usage) */}
      {!embedded && renderUploadForm()}

      {!hideContractList ? (
        <div className={embedded ? "" : "mt-8"}>
          <div className="cms-provider-contract-table-header">
            <div className="cms-provider-contract-table-header-actions">
              {embedded && contracts.length > 1 && (
                <button
                  type="button"
                  onClick={() => setShowAllContracts((prev) => !prev)}
                  className="cms-provider-contract-view-all-btn"
                >
                  {showAllContracts ? "Show Less" : "View All"}
                </button>
              )}
            </div>
          </div>

          <div className="cms-provider-contract-table-wrap">
            <table className="cms-provider-contract-table">
              <thead>
                <tr>
                  <th>Document Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleContracts.length > 0 ? (
                  visibleContracts.map((contract) => (
                    <tr key={contract.id}>
                      <td>
                        <span className="cms-provider-contract-doc-name">
                          {contract.document_name}
                        </span>
                      </td>
                      <td>{formatDate(contract.start_date)}</td>
                      <td>{formatDate(contract.end_date)}</td>
                      <td>
                        <span
                          className={`cms-provider-contract-status-badge ${
                            contract.status === "active"
                              ? "cms-provider-contract-status-badge--active"
                              : "cms-provider-contract-status-badge--expired"
                          }`}
                        >
                          {contract.status}
                        </span>
                      </td>
                      <td
                        style={{
                          maxWidth: "180px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#64748b",
                        }}
                        title={contract.notes || ""}
                      >
                        {contract.notes || "-"}
                      </td>
                      <td>
                        <div className="cms-provider-contract-table-actions">
                          <button
                            type="button"
                            className="cms-provider-table-icon cms-provider-table-icon--view"
                            title="View"
                            onClick={() => window.open(contract.url, "_blank")}
                          >
                            <FaIcons.FaEye size={11} />
                          </button>
                          <button
                            type="button"
                            className="cms-provider-table-icon cms-provider-table-icon--delete"
                            title="Delete"
                            onClick={() => handleDelete(contract)}
                          >
                            <FaIcons.FaTrashAlt size={11} />
                          </button>
                          <button
                            type="button"
                            className={`cms-provider-table-icon cms-provider-table-icon--active ${
                              contract.status === "active" ? "is-selected" : ""
                            }`}
                            title="Mark Active"
                            onClick={() => updateStatus(contract.id, "active")}
                          >
                            <FaIcons.FaCheckCircle size={11} />
                          </button>
                          <button
                            type="button"
                            className={`cms-provider-table-icon cms-provider-table-icon--inactive ${
                              contract.status === "expired" ? "is-selected" : ""
                            }`}
                            title="Mark Inactive"
                            onClick={() => updateStatus(contract.id, "expired")}
                          >
                            <FaIcons.FaBan size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="cms-provider-contract-empty">
                      <FaIcons.FaFileAlt
                        style={{
                          display: "block",
                          margin: "0 auto 8px",
                          fontSize: "22px",
                          color: "#cbd5e1",
                        }}
                      />
                      No contracts found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {embedded && !hideContractList ? (
        <Modal
          show={showUploadModal}
          onHide={closeUploadModal}
          centered
          size="lg"
          backdrop={uploading ? "static" : true}
          keyboard={!uploading}
          backdropClassName="cms-provider-contract-modal-backdrop"
          dialogClassName="cms-provider-contract-modal-dialog"
          contentClassName="cms-provider-contract-modal-content tailwind-container"
          className="cms-provider-contract-modal"
        >
          <Modal.Header
            closeButton={false}
            className="cms-provider-contract-modal-header cms-provider-contract-modal-header--with-close"
          >
            <Modal.Title className="cms-provider-contract-modal-title">
              Upload Provider Contract
            </Modal.Title>
            <button
              type="button"
              className="cms-provider-contract-modal-dismiss-btn"
              onClick={closeUploadModal}
              disabled={uploading}
              aria-label="Close upload modal"
            >
              <FaIcons.FaTimes size={12} />
            </button>
          </Modal.Header>
          <Modal.Body className="cms-provider-contract-modal-body">
            {renderUploadForm()}
          </Modal.Body>
          <Modal.Footer className="cms-provider-contract-modal-footer">
            <button
              type="button"
              className="cms-provider-contract-modal-close-btn"
              onClick={closeUploadModal}
              disabled={uploading}
            >
              Cancel
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default UploadProviderContract;
